import Api, { ErrorMode, FetchMethodType } from './Api';

export interface SendSmsCodeData {
  phoneNumber: string;
}

export interface SendEmailCodeData {
  phoneNumber: string;
  email: string;
}

export interface SendSmsCodeForApplicationIdData {
  applicationId: string;
}

export interface SendSmsCodeResponse {
  phoneId: string;
}

export interface HStreamCallbackData {
  code: string;
}

export interface HStreamTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

export interface HStreamUserInfo {
  sub: string;
  identities: string;
  emailVerified: string;
  givenName: string;
  familyName: string;
  hstreamId: string;
  email: string;
  userId: string;
  username: string;
}

export interface HStreamCallbackResponse {
  tokens: HStreamTokens;
  userInfo: HStreamUserInfo;
}

export interface SendEmailCodeResponse {
  emailId: string;
}

export interface AuthOtpData {
  phoneOrEmailId: string;
  code: string;
  phoneNumber?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  credential?: string;
}

export type AuthOtpResponse =
  | {
      verified: false;
    }
  | {
      verified: true;
      sessionToken: string;
      firstName?: string;
      lastName?: string;
      credential?: string;
      canReapply?: boolean;
    };

export interface ReapplyData {
  sessionToken: string;
  firstName: string;
  lastName: string;
  credential?: string;
}

export interface ReapplyAddress {
  street: string;
  city: string;
  stateOrProvince: string;
  zipOrPostalCode: string;
  country: string;
}

export interface ReapplyResponse {
  email: string;
  address: ReapplyAddress;
  primaryEmployerName: string;
  notEmployed: boolean;
  additionalIncome?: number;
  totalAnnualIncome: number;
  credentialsAcquisitionDate?: string;
  dateOfBirth: string;
  last4SSN: string;
}

export enum ErrorType {
  DifferentEmailSession = 'differentEmailSession',
  DifferentPhoneNumberSession = 'differentPhoneNumberSession',
}

export type AuthOtpResponseError = {
  status: number;
  error: string;
  errorType: ErrorType;
};

export interface AuthSessionData {
  sessionToken: string;
}

export type AuthSessionResponse =
  | {
      authenticated: false;
    }
  | {
      authenticated: true;
      phoneNumber: string;
    };

export interface IdentityPrefillData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export interface MaskedPrefillFields {
  email: string;
  phone: string;
  address: string;
  ssn: string;
  dob: string;
  name: string;
}

export interface PrefillClientPayload {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  last4SSN: string;
  masked: MaskedPrefillFields;
}

export interface IdentityPrefillStartResponse {
  prefillSessionId: string;
  otpRequired: boolean;
  verified?: true;
  sessionToken?: string;
  emailPresent?: boolean;
  crsUserId?: string;
  prefillContinuationToken?: string;
  prefill?: PrefillClientPayload;
  canReapply?: boolean;
}

export interface IdentitySubmitOtpData {
  prefillSessionId: string;
  code: string;
}

export type IdentitySubmitOtpResponse =
  | { verified: false }
  | {
      verified: true;
      sessionToken: string;
      emailPresent: boolean;
      crsUserId: string;
      prefillContinuationToken: string;
      prefill: PrefillClientPayload;
      canReapply?: boolean;
    };

export interface IdentityResendOtpData {
  prefillSessionId: string;
}

export interface AuthApi {
  sendSmsCode(data: SendSmsCodeData): Promise<SendSmsCodeResponse>;
  sendSmsCodeForApplicationId(data: SendSmsCodeForApplicationIdData): Promise<SendSmsCodeResponse>;
  sendEmailCode(data: SendEmailCodeData): Promise<SendEmailCodeResponse>;
  authOtp(data: AuthOtpData): Promise<AuthOtpResponse>;
  reapply(data: ReapplyData): Promise<ReapplyResponse>;
  authSession(data: AuthSessionData): Promise<AuthSessionResponse>;
  hstreamCallback(data: HStreamCallbackData): Promise<HStreamCallbackResponse>;
  identityPrefill(data: IdentityPrefillData): Promise<IdentityPrefillStartResponse>;
  identitySubmitOtp(data: IdentitySubmitOtpData): Promise<IdentitySubmitOtpResponse>;
  identityResendOtp(data: IdentityResendOtpData): Promise<{ success: true }>;
}

export default class AuthRestApi extends Api implements AuthApi {
  async sendSmsCode(data: SendSmsCodeData): Promise<SendSmsCodeResponse> {
    return this.fetch<SendSmsCodeResponse>('/auth/sendSmsCode', {
      method: FetchMethodType.POST,
      body: data,
    });
  }

  async sendSmsCodeForApplicationId(data: SendSmsCodeForApplicationIdData): Promise<SendSmsCodeResponse> {
    return this.fetch<SendSmsCodeResponse>('/auth/sendSmsCodeForApplicationId', {
      method: FetchMethodType.POST,
      body: data,
    });
  }

  async authOtp(data: AuthOtpData): Promise<AuthOtpResponse> {
    return this.fetch<AuthOtpResponse>(`/auth/authOtp`, {
      method: FetchMethodType.POST,
      body: data,
      errorMode: ErrorMode.HideError,
      redirectToLogin: false,
    });
  }

  async reapply(data: ReapplyData): Promise<ReapplyResponse> {
    return this.fetch<ReapplyResponse>('/auth/reapply', {
      method: FetchMethodType.POST,
      body: data,
      errorMode: ErrorMode.HideError,
      redirectToLogin: false,
      maxRetries: 0,
    });
  }

  async sendEmailCode(data: SendEmailCodeData): Promise<SendEmailCodeResponse> {
    return this.fetch<SendEmailCodeResponse>('/auth/sendEmailCode', {
      method: FetchMethodType.POST,
      body: data,
    });
  }

  async authSession(data: AuthSessionData): Promise<AuthSessionResponse> {
    return this.fetch<AuthSessionResponse>(`/auth/authSession?sessionToken=${encodeURIComponent(data.sessionToken)}`, {
      method: FetchMethodType.GET,
    });
  }

  async hstreamCallback(data: HStreamCallbackData): Promise<HStreamCallbackResponse> {
    return this.fetch<HStreamCallbackResponse>('/auth/hstream/callback', {
      method: FetchMethodType.POST,
      body: data,
      errorMode: ErrorMode.DisplayActualMessage,
    });
  }

  async identityPrefill(data: IdentityPrefillData): Promise<IdentityPrefillStartResponse> {
    return this.fetch<IdentityPrefillStartResponse>('/auth/identityPrefill', {
      method: FetchMethodType.POST,
      body: data,
    });
  }

  async identitySubmitOtp(data: IdentitySubmitOtpData): Promise<IdentitySubmitOtpResponse> {
    return this.fetch<IdentitySubmitOtpResponse>('/auth/identitySubmitOtp', {
      method: FetchMethodType.POST,
      body: data,
      errorMode: ErrorMode.HideError,
      redirectToLogin: false,
    });
  }

  async identityResendOtp(data: IdentityResendOtpData): Promise<{ success: true }> {
    return this.fetch<{ success: true }>('/auth/identityResendOtp', {
      method: FetchMethodType.POST,
      body: data,
    });
  }
}
